import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, X, Image, Edit3, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-logger";
import { uploadAndGetSignedUrl, refreshSignedUrls, validateFileSize } from "@/lib/storage";

export default function GalleryPage() {
  const { user, profile } = useAuth();
  const [photos, setPhotos] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [caption, setCaption] = useState("");
  const [showCaptionInput, setShowCaptionInput] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [editCaptionText, setEditCaptionText] = useState("");

  const workspaceId = profile?.workspace_id;

  useEffect(() => {
    if (!workspaceId) return;
    supabase.from("gallery_photos").select("*").eq("workspace_id", workspaceId).order("created_at", { ascending: false })
      .then(async ({ data }) => {
        const withUrls = await refreshSignedUrls(data || [], "image_url");
        setPhotos(withUrls);
        setLoading(false);
      });
  }, [workspaceId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const sizeErr = validateFileSize(file);
    if (sizeErr) { toast.error(sizeErr); return; }
    setPendingFile(file);
    setShowCaptionInput(true);
  };

  const handleUpload = async () => {
    if (!pendingFile || !workspaceId) return;
    setUploading(true);
    setShowCaptionInput(false);
    try {
      const ext = pendingFile.name.split(".").pop();
      const path = `${workspaceId}/${Date.now()}.${ext}`;
      const { signedUrl, error: upErr } = await uploadAndGetSignedUrl("couple-uploads", path, pendingFile);
      if (upErr || !signedUrl) {
        toast.error("Upload failed", { description: upErr || "Unknown error" });
        setUploading(false); setPendingFile(null); return;
      }
      const { data, error } = await supabase.from("gallery_photos").insert({
        workspace_id: workspaceId, uploaded_by: user!.id, image_url: path, caption: caption.trim() || null,
      }).select().single();
      if (error) { toast.error("Couldn't save", { description: error.message }); }
      else {
        setPhotos([{ ...data, image_url: signedUrl }, ...photos]);
        logActivity("memory_uploaded", { photo_id: data.id });
        toast.success("Photo added ✨");
      }
    } catch (e: any) {
      toast.error("Upload failed", { description: e.message });
    }
    setUploading(false); setPendingFile(null); setCaption("");
  };

  const deletePhoto = async (id: string) => {
    await supabase.from("gallery_photos").delete().eq("id", id);
    setPhotos(photos.filter((p) => p.id !== id));
  };

  const saveCaption = async (id: string) => {
    await supabase.from("gallery_photos").update({ caption: editCaptionText.trim() || null }).eq("id", id);
    setPhotos(photos.map((p) => (p.id === id ? { ...p, caption: editCaptionText.trim() || null } : p)));
    setEditingCaption(null);
  };

  return (
    <div className="space-y-8 max-w-full">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">Our Memories</h1>
        <p className="mt-2 text-muted-foreground text-lg">Photos that remind us of what matters.</p>
      </motion.div>

      <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 cursor-pointer">
        <Plus className="w-4 h-4" /> {uploading ? "Uploading..." : "Add Photo"}
        <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
      </label>

      {showCaptionInput && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3">
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a caption (optional)" className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
          <div className="flex gap-2">
            <button onClick={handleUpload} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">Upload</button>
            <button onClick={() => { setShowCaptionInput(false); setPendingFile(null); setCaption(""); }} className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading photos...</span>
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center py-16">
          <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
          <p className="text-muted-foreground">No photos yet. Upload your first memory together.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo, i) => (
            <motion.div key={photo.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.03 }} className="relative group rounded-xl overflow-hidden bg-secondary">
              <div className="aspect-square">
                <img src={photo.image_url} alt={photo.caption || "Memory"} className="w-full h-full object-cover" loading="lazy" />
              </div>
              {editingCaption === photo.id ? (
                <div className="absolute bottom-0 inset-x-0 bg-background/90 p-2 flex gap-1">
                  <input value={editCaptionText} onChange={(e) => setEditCaptionText(e.target.value)} className="flex-1 bg-background border border-input rounded px-2 py-1 text-xs focus:outline-none min-w-0" autoFocus />
                  <button onClick={() => saveCaption(photo.id)} className="p-1 text-primary flex-shrink-0"><Check className="w-3.5 h-3.5" /></button>
                </div>
              ) : photo.caption ? (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-foreground/60 to-transparent p-2">
                  <p className="text-xs text-primary-foreground truncate">{photo.caption}</p>
                </div>
              ) : null}
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <button onClick={() => { setEditingCaption(photo.id); setEditCaptionText(photo.caption || ""); }} className="p-1.5 rounded-full bg-background/80 hover:bg-background"><Edit3 className="w-3 h-3 text-foreground" /></button>
                <button onClick={() => deletePhoto(photo.id)} className="p-1.5 rounded-full bg-background/80 hover:bg-background"><X className="w-3.5 h-3.5 text-foreground" /></button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
